import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const Lightbox = ({ images = [], index = 0, open, onClose, onChange }) => {
  const next = useCallback(
    (e) => {
      e?.stopPropagation();
      onChange((index + 1) % images.length);
    },
    [index, images.length, onChange]
  );
  const prev = useCallback(
    (e) => {
      e?.stopPropagation();
      onChange((index - 1 + images.length) % images.length);
    },
    [index, images.length, onChange]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, next, prev, onClose]);

  const current = images[index];

  return (
    <AnimatePresence>
      {open && current && (
        <motion.div
          data-testid="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center grain"
        >
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-10"
            aria-label="Close"
          >
            <X size={30} />
          </button>

          <span className="absolute top-7 left-6 font-accent uppercase text-xs tracking-[0.25em] text-white/60 z-10">
            {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </span>

          {images.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={prev}
                className="absolute left-3 md:left-8 text-white/60 hover:text-white transition-colors z-10"
                aria-label="Previous"
              >
                <ChevronLeft size={48} strokeWidth={1} />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={next}
                className="absolute right-3 md:right-8 text-white/60 hover:text-white transition-colors z-10"
                aria-label="Next"
              >
                <ChevronRight size={48} strokeWidth={1} />
              </button>
            </>
          )}

          <AnimatePresence mode="wait">
            <motion.img
              key={current}
              src={current}
              alt={`Frame ${index + 1}`}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[86vh] max-w-[90vw] object-contain"
            />
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Lightbox;
